import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IAssemblyApplicationResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import { AssemblyApplication } from 'pages/Settings/AssemblyType/AssemblyApplication';
import { AssemblyParameterDetailTree } from 'pages/Settings/AssemblyType/AssemblyParameterDetailTree';
import { CreateAssemblyApplicationModal } from 'pages/Settings/AssemblyType/CreateAssemblyApplicationModal';
import { CreateAssemblyParameterModal } from 'pages/Settings/AssemblyType/CreateAssemblyParameterModal';
import { DuplicateAssemblyApplicationModal } from 'pages/Settings/AssemblyType/DuplicateAssemblyApplicationModal';
import { EditAssemblyApplicationModal } from 'pages/Settings/AssemblyType/EditAssemblyApplicationModal';
import {
    ApplicationsContainer,
    Column,
    ColumnContent,
    ColumnHeader,
    ColumnsSection,
    HeaderRow,
    ScrollableContent,
    ScrollableInnerContent,
} from 'pages/Settings/AssemblyType/styled';
import { useAssemblyApplications } from 'pages/Settings/AssemblyType/useAssemblyApplications';
import { useEffect, useMemo, useState } from 'react';
import { StyledButton } from 'shared/button/Button';
import { extractErrorString } from 'utils/api';
import { toggleArrayItem } from 'utils/array';

export type AssemblyApplicationParameterDetailsProps = Readonly<{
    assemblyTypeId: number;
}>;

export function AssemblyApplicationParameterDetails({
    assemblyTypeId,
}: AssemblyApplicationParameterDetailsProps) {
    const { canEditAssemblyType } = usePermissions();
    const { showToast } = useNotificationContext();

    const [selectedApplicationId, setSelectedApplicationId] = useState<number>();
    const [expandedParameterCodes, setExpandedParameterCodes] = useState<string[]>([]);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isCreateParameterModalOpen, setIsCreateParameterModalOpen] = useState(false);
    const [applicationToEdit, setApplicationToEdit] =
        useState<IAssemblyApplicationResponse>();
    const [applicationToDelete, setApplicationToDelete] =
        useState<IAssemblyApplicationResponse>();
    const [applicationToDuplicate, setApplicationToDuplicate] =
        useState<IAssemblyApplicationResponse>();
    const [applicationDeleteError, setApplicationDeleteError] = useState<string>();

    const [filterText, setFilterText] = useState('');

    const {
        results: applications,
        isLoading: isLoadingApplications,
        refetch: refetchApplications,
        setFilter,
    } = useAssemblyApplications(assemblyTypeId);

    const selectedApplication = useMemo(
        () =>
            applications?.find((application) => application.Id === selectedApplicationId),
        [applications, selectedApplicationId],
    );

    const { mutate: deleteApplication, isPending: isDeleting } = useMutation({
        mutationFn: async () => {
            if (!applicationToDelete) {
                throw new Error('Expected application');
            }
            return sdClient.deleteAssemblyApplication({
                applicationId: applicationToDelete.Id,
            });
        },

        onSuccess: () => {
            setApplicationToDelete(undefined);
            setSelectedApplicationId(undefined);
            setExpandedParameterCodes([]);
            refetchApplications();
            showToast({
                type: 'success',
                content: 'Successfully deleted assembly application',
            });
        },

        onError: (response) => {
            showToast({
                type: 'error',
                content: 'Failed to delete assembly application',
            });
            setApplicationDeleteError(extractErrorString(response));
        },
    });

    const selectApplication = (applicationId: number | undefined) => {
        setSelectedApplicationId(applicationId);
        setExpandedParameterCodes([]);
    };

    const onCreateParameterClose = (
        updatedApplication?: IAssemblyApplicationResponse,
    ) => {
        setIsCreateParameterModalOpen(false);
        if (updatedApplication) {
            refetchApplications();
        }
    };

    const onCreateOrEditModalClose = (
        application: IAssemblyApplicationResponse | undefined,
    ) => {
        setIsCreateModalOpen(false);
        setApplicationToEdit(undefined);
        setApplicationToDuplicate(undefined);
        selectApplication(application?.Id);
        if (application) {
            refetchApplications();
        }
    };

    const onDeleteApplicationClose = () => {
        setApplicationDeleteError(undefined);
        setApplicationToDelete(undefined);
    };

    useEffectSkipFirst(() => {
        setFilter((filter) => ({ ...filter, nameSearch: filterText.trim() }));
    }, [filterText, setFilter]);

    useEffectSkipFirst(() => {
        setFilterText('');
        selectApplication(undefined);
    }, [assemblyTypeId, setSelectedApplicationId]);

    useEffect(() => {
        if (applications && applications.length > 0 && !selectedApplicationId) {
            selectApplication(applications[0].Id);
        }
    }, [applications, selectedApplicationId]);

    useEffect(() => setApplicationDeleteError(undefined), [assemblyTypeId]);

    return (
        <>
            <SpinnerArea isLoading={isLoadingApplications}>
                <ColumnsSection>
                    <Column>
                        <ColumnHeader>
                            <HeaderRow>
                                <div className="bold flex-grow-1">Applications</div>
                                {canEditAssemblyType && (
                                    <StyledButton
                                        icon={<FontAwesomeIcon icon={faPlus} />}
                                        appearance="primary"
                                        spacing="compact"
                                        onClick={() => setIsCreateModalOpen(true)}
                                    >
                                        Create Application
                                    </StyledButton>
                                )}
                            </HeaderRow>
                            <DebouncedSearchInput
                                value={filterText}
                                onChange={(newFilterText) =>
                                    setFilterText(newFilterText.toLowerCase())
                                }
                                placeholder="Search Applications"
                            />
                        </ColumnHeader>
                        <ColumnContent>
                            <ScrollableContent>
                                <ScrollableInnerContent>
                                    <ApplicationsContainer>
                                        {applications &&
                                            applications.length === 0 &&
                                            'No applications found'}
                                        {applications?.map((application) => (
                                            <AssemblyApplication
                                                key={application.Id}
                                                filterText={filterText}
                                                application={application}
                                                isSelected={
                                                    application.Id ===
                                                    selectedApplication?.Id
                                                }
                                                onSelect={() =>
                                                    selectApplication(application.Id)
                                                }
                                                onApplicationEditClick={() =>
                                                    setApplicationToEdit(application)
                                                }
                                                onApplicationDeleteClick={() =>
                                                    setApplicationToDelete(application)
                                                }
                                                onApplicationDuplicateClick={() =>
                                                    setApplicationToDuplicate(application)
                                                }
                                            />
                                        ))}
                                    </ApplicationsContainer>
                                </ScrollableInnerContent>
                            </ScrollableContent>
                        </ColumnContent>
                    </Column>
                    <Column>
                        <ColumnHeader>
                            {selectedApplication && (
                                <HeaderRow>
                                    <div className="bold flex-grow-1">
                                        Parameters: {selectedApplication.Name}
                                    </div>
                                    {canEditAssemblyType && (
                                        <StyledButton
                                            icon={<FontAwesomeIcon icon={faPlus} />}
                                            appearance="subtle"
                                            spacing="compact"
                                            onClick={() =>
                                                setIsCreateParameterModalOpen(true)
                                            }
                                            className="ml-10"
                                        >
                                            Create
                                        </StyledButton>
                                    )}
                                </HeaderRow>
                            )}
                        </ColumnHeader>
                        <ColumnContent>
                            <ScrollableContent>
                                <ScrollableInnerContent>
                                    {selectedApplication && (
                                        <AssemblyParameterDetailTree
                                            application={selectedApplication}
                                            expandedParameterCodes={
                                                expandedParameterCodes
                                            }
                                            onParameterExpand={(parameterCode) =>
                                                setExpandedParameterCodes((current) =>
                                                    toggleArrayItem(
                                                        current,
                                                        parameterCode,
                                                    ),
                                                )
                                            }
                                            onChange={refetchApplications}
                                        />
                                    )}
                                </ScrollableInnerContent>
                            </ScrollableContent>
                        </ColumnContent>
                    </Column>
                </ColumnsSection>
            </SpinnerArea>
            {isCreateModalOpen && (
                <CreateAssemblyApplicationModal
                    siblings={applications ?? []}
                    assemblyTypeId={assemblyTypeId}
                    onClose={onCreateOrEditModalClose}
                />
            )}
            {applicationToEdit && (
                <EditAssemblyApplicationModal
                    application={applicationToEdit}
                    siblings={
                        applications?.filter(
                            (application) => application.Id !== applicationToEdit.Id,
                        ) ?? []
                    }
                    onClose={onCreateOrEditModalClose}
                />
            )}
            {applicationToDuplicate && (
                <DuplicateAssemblyApplicationModal
                    siblings={applications ?? []}
                    assemblyTypeId={assemblyTypeId}
                    onClose={onCreateOrEditModalClose}
                    application={applicationToDuplicate}
                />
            )}
            {applicationToDelete && (
                <ConfirmModal
                    title={`Delete '${applicationToDelete.Name}'`}
                    actionText="delete this application? Other users in your organization may be referencing it."
                    checkText="delete this application"
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting && !applicationDeleteError}
                    onAction={deleteApplication}
                    error={applicationDeleteError}
                    onClose={() => onDeleteApplicationClose()}
                />
            )}
            {isCreateParameterModalOpen && selectedApplication && (
                <CreateAssemblyParameterModal
                    application={selectedApplication}
                    onClose={onCreateParameterClose}
                />
            )}
        </>
    );
}
